import { Position, Cell, Zone, ZoneType, WallSegment } from '../types/maze-types';
import { MazeImagePair } from '../assets/mazeImages';
import { generateShapedMaze, getRandomShape, MazeShape } from './shapeGenerator';

/**
 * Создание сетки лабиринта
 */
function createMazeGrid(rows: number, cols: number): Cell[][] {
  const grid: Cell[][] = [];
  
  // Create cells with walls
  for (let y = 0; y < rows; y++) {
    const row: Cell[] = [];
    for (let x = 0; x < cols; x++) {
      row.push({
        x,
        y,
        walls: { top: true, right: true, bottom: true, left: true },
        visited: false,
      });
    }
    grid.push(row);
  }
  
  return grid;
}

/**
 * Улучшенная генерация лабиринта с параметрами сложности
 */
function generateMazeWithComplexity(
  grid: Cell[][], 
  rows: number, 
  cols: number,
  complexity: number,
  deadEndDensity: number,
  branchingFactor: number,
  wallRemovalChance: number
): void {
  const stack: Cell[] = [];
  
  // Находим первую ячейку, которая не является частью зоны
  let startCell: Cell | null = null;
  for (let y = 0; y < rows && !startCell; y++) {
    for (let x = 0; x < cols && !startCell; x++) {
      if (!grid[y][x].isEntranceZone && !grid[y][x].isExitZone) {
        startCell = grid[y][x];
      }
    }
  }
  
  // Если не нашли свободную ячейку, используем первую доступную
  if (!startCell) {
    startCell = grid[0][0];
  }
  
  startCell.visited = true;
  stack.push(startCell);
  
  while (stack.length > 0) {
    const current = stack[stack.length - 1];
    const neighbors = getNeighborsDuringGeneration(current, grid, rows, cols);
    
    if (neighbors.length > 0) {
      // Выбираем соседа с учетом коэффициента ветвления
      let chosenNeighbor: Cell;
      
      if (Math.random() < complexity && neighbors.length > 1) {
        // Для высокой сложности выбираем случайного соседа
        chosenNeighbor = neighbors[Math.floor(Math.random() * neighbors.length)];
      } else {
        // Для низкой сложности предпочитаем прямые пути
        chosenNeighbor = neighbors[0];
      }
      
      chosenNeighbor.visited = true;
      removeWalls(current, chosenNeighbor);
      stack.push(chosenNeighbor);
      
      // Создаем дополнительные ветвления с учетом branchingFactor
      if (Math.random() < branchingFactor / 10 && neighbors.length > 1) {
        const additionalNeighbors = neighbors.filter(n => n !== chosenNeighbor);
        for (const neighbor of additionalNeighbors.slice(0, Math.floor(branchingFactor / 2))) {
          if (Math.random() < 0.3) {
            neighbor.visited = true;
            removeWalls(current, neighbor);
            stack.push(neighbor);
          }
        }
      }
    } else {
      // Тупик - решаем, оставить ли его
      if (Math.random() > deadEndDensity) {
        // Пытаемся создать дополнительные проходы
        const allNeighbors = getAllNeighbors(current, grid, rows, cols);
        for (const neighbor of allNeighbors) {
          if (Math.random() < wallRemovalChance) {
            removeWalls(current, neighbor);
          }
        }
      }
      stack.pop();
    }
  }
  
  // Дополнительное удаление стен для создания циклов
  if (wallRemovalChance > 0) {
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const cell = grid[y][x];
        const neighbors = getAllNeighbors(cell, grid, rows, cols);
        
        for (const neighbor of neighbors) {
          if (Math.random() < wallRemovalChance) {
            removeWalls(cell, neighbor);
          }
        }
      }
    }
  }
}

/**
 * Получение всех соседей (включая посещенных)
 */
function getAllNeighbors(cell: Cell, grid: Cell[][], rows: number, cols: number): Cell[] {
  const neighbors: Cell[] = [];
  const { x, y } = cell;
  
  if (y > 0) neighbors.push(grid[y - 1][x]);
  if (x < cols - 1) neighbors.push(grid[y][x + 1]);
  if (y < rows - 1) neighbors.push(grid[y + 1][x]);
  if (x > 0) neighbors.push(grid[y][x - 1]);
  
  return neighbors;
}

/**
 * Получение непосещенных соседей для генерации лабиринта
 */
function getNeighborsDuringGeneration(cell: Cell, grid: Cell[][], rows: number, cols: number): Cell[] {
  const neighbors: Cell[] = [];
  const { x, y } = cell;
  
  // Check top neighbor
  if (y > 0 && !grid[y - 1][x].visited) {
    neighbors.push(grid[y - 1][x]);
  }
  
  // Check right neighbor
  if (x < cols - 1 && !grid[y][x + 1].visited) {
    neighbors.push(grid[y][x + 1]);
  }
  
  // Check bottom neighbor
  if (y < rows - 1 && !grid[y + 1][x].visited) {
    neighbors.push(grid[y + 1][x]);
  }
  
  // Check left neighbor
  if (x > 0 && !grid[y][x - 1].visited) {
    neighbors.push(grid[y][x - 1]);
  }
  
  return neighbors;
}

/**
 * Удаление стен между двумя ячейками
 */
function removeWalls(current: Cell, next: Cell): void {
  const dx = current.x - next.x;
  const dy = current.y - next.y;
  
  if (dx === 1) {
    current.walls.left = false;
    next.walls.right = false;
  } else if (dx === -1) {
    current.walls.right = false;
    next.walls.left = false;
  }
  
  if (dy === 1) {
    current.walls.top = false;
    next.walls.bottom = false;
  } else if (dy === -1) {
    current.walls.bottom = false;
    next.walls.top = false;
  }
}

/**
 * Создание зоны входа или выхода в лабиринте
 */
function createZone(grid: Cell[][], type: ZoneType, size: number, rows: number, cols: number): Zone {
  let topLeft: Position;
  let entranceCell: Position;
  
  // Выбираем сторону периметра для зоны
  if (type === ZoneType.ENTRANCE) {
    // Выбираем одну из четырёх сторон: 0-верх, 1-право, 2-низ, 3-лево
    const side = Math.floor(Math.random() * 4);

    switch (side) {
      case 0: // Верхняя сторона
        topLeft = {
          x: Math.floor(Math.random() * (cols - size + 1)),
          y: 0
        };
        break;
      case 1: // Правая сторона
        topLeft = {
          x: Math.max(0, cols - size),
          y: Math.floor(Math.random() * (rows - size + 1))
        };
        break;
      case 2: // Нижняя сторона
        topLeft = {
          x: Math.floor(Math.random() * (cols - size + 1)),
          y: Math.max(0, rows - size)
        };
        break;
      case 3: // Левая сторона
        topLeft = {
          x: 0,
          y: Math.floor(Math.random() * (rows - size + 1))
        };
        break;
      default:
        topLeft = { x: 0, y: 0 };
    }
    const isTopCorner = topLeft.y < rows / 2;
    const isLeftCorner = topLeft.x < cols / 2;
    
    // Удаляем все стены внутри зоны и помечаем ячейки
    for (let y = topLeft.y; y < Math.min(rows, topLeft.y + size); y++) {
      for (let x = topLeft.x; x < Math.min(cols, topLeft.x + size); x++) {
        grid[y][x].isEntranceZone = true;
        
        // Удаляем все внутренние стены в зоне
        if (x < topLeft.x + size - 1) {
          grid[y][x].walls.right = false;
          if (x + 1 < cols) grid[y][x + 1].walls.left = false;
        }
        
        if (y < topLeft.y + size - 1) {
          grid[y][x].walls.bottom = false;
          if (y + 1 < rows) grid[y + 1][x].walls.top = false;
        }
      }
    }
    
    // Создаем стены вокруг зоны
    const rightBorder = Math.min(cols - 1, topLeft.x + size - 1);
    const bottomBorder = Math.min(rows - 1, topLeft.y + size - 1);
    const leftBorder = topLeft.x;
    const topBorder = topLeft.y;

    // Укрепляем все границы зоны
    // Верхняя граница
    for (let x = leftBorder; x <= rightBorder; x++) {
      grid[topBorder][x].walls.top = true;
      if (topBorder > 0) grid[topBorder - 1][x].walls.bottom = true;
    }
    // Нижняя граница
    for (let x = leftBorder; x <= rightBorder; x++) {
      grid[bottomBorder][x].walls.bottom = true;
      if (bottomBorder + 1 < rows) grid[bottomBorder + 1][x].walls.top = true;
    }
    // Левая граница
    for (let y = topBorder; y <= bottomBorder; y++) {
      grid[y][leftBorder].walls.left = true;
      if (leftBorder > 0) grid[y][leftBorder - 1].walls.right = true;
    }
    // Правая граница
    for (let y = topBorder; y <= bottomBorder; y++) {
      grid[y][rightBorder].walls.right = true;
      if (rightBorder + 1 < cols) grid[y][rightBorder + 1].walls.left = true;
    }

    // Создаем единственный проход в лабиринт
    // Определяем возможные направления выхода
    const possibleExits: Array<{dir: string, canExit: boolean}> = [
      { dir: 'top', canExit: topBorder > 0 },
      { dir: 'bottom', canExit: bottomBorder + 1 < rows },
      { dir: 'left', canExit: leftBorder > 0 },
      { dir: 'right', canExit: rightBorder + 1 < cols }
    ];

    // Фильтруем только доступные направления
    const availableExits = possibleExits.filter(e => e.canExit);

    // Инициализируем дефолтным значением
    entranceCell = topLeft;

    if (availableExits.length > 0) {
      // Выбираем случайное направление
      const chosenExit = availableExits[Math.floor(Math.random() * availableExits.length)];

      switch (chosenExit.dir) {
        case 'top':
          const randomXTop = Math.floor(Math.random() * size) + leftBorder;
          entranceCell = { x: randomXTop, y: topBorder };
          grid[topBorder][randomXTop].walls.top = false;
          grid[topBorder - 1][randomXTop].walls.bottom = false;
          break;
        case 'bottom':
          const randomXBottom = Math.floor(Math.random() * size) + leftBorder;
          entranceCell = { x: randomXBottom, y: bottomBorder };
          grid[bottomBorder][randomXBottom].walls.bottom = false;
          grid[bottomBorder + 1][randomXBottom].walls.top = false;
          break;
        case 'left':
          const randomYLeft = Math.floor(Math.random() * size) + topBorder;
          entranceCell = { x: leftBorder, y: randomYLeft };
          grid[randomYLeft][leftBorder].walls.left = false;
          grid[randomYLeft][leftBorder - 1].walls.right = false;
          break;
        case 'right':
          const randomYRight = Math.floor(Math.random() * size) + topBorder;
          entranceCell = { x: rightBorder, y: randomYRight };
          grid[randomYRight][rightBorder].walls.right = false;
          grid[randomYRight][rightBorder + 1].walls.left = false;
          break;
      }
    }

    // Отмечаем ячейку входа
    if (entranceCell.y < rows && entranceCell.x < cols) {
      grid[entranceCell.y][entranceCell.x].isEntrance = true;
    }

  } else { // ZoneType.EXIT
    // Выбираем одну из четырёх сторон: 0-верх, 1-право, 2-низ, 3-лево
    const side = Math.floor(Math.random() * 4);

    switch (side) {
      case 0: // Верхняя сторона
        topLeft = {
          x: Math.floor(Math.random() * (cols - size + 1)),
          y: 0
        };
        break;
      case 1: // Правая сторона
        topLeft = {
          x: Math.max(0, cols - size),
          y: Math.floor(Math.random() * (rows - size + 1))
        };
        break;
      case 2: // Нижняя сторона
        topLeft = {
          x: Math.floor(Math.random() * (cols - size + 1)),
          y: Math.max(0, rows - size)
        };
        break;
      case 3: // Левая сторона
        topLeft = {
          x: 0,
          y: Math.floor(Math.random() * (rows - size + 1))
        };
        break;
      default:
        topLeft = { x: Math.max(0, cols - size), y: Math.max(0, rows - size) };
    }

    // Удаляем все стены внутри зоны и помечаем ячейки
    for (let y = topLeft.y; y < Math.min(rows, topLeft.y + size); y++) {
      for (let x = topLeft.x; x < Math.min(cols, topLeft.x + size); x++) {
        grid[y][x].isExitZone = true;

        // Удаляем все внутренние стены в зоне
        if (x < topLeft.x + size - 1) {
          grid[y][x].walls.right = false;
          if (x + 1 < cols) grid[y][x + 1].walls.left = false;
        }

        if (y < topLeft.y + size - 1) {
          grid[y][x].walls.bottom = false;
          if (y + 1 < rows) grid[y + 1][x].walls.top = false;
        }
      }
    }

    // Создаем стены вокруг зоны
    const rightBorder = Math.min(cols - 1, topLeft.x + size - 1);
    const bottomBorder = Math.min(rows - 1, topLeft.y + size - 1);
    const leftBorder = topLeft.x;
    const topBorder = topLeft.y;

    // Укрепляем все границы зоны
    // Верхняя граница
    for (let x = leftBorder; x <= rightBorder; x++) {
      grid[topBorder][x].walls.top = true;
      if (topBorder > 0) grid[topBorder - 1][x].walls.bottom = true;
    }
    // Нижняя граница
    for (let x = leftBorder; x <= rightBorder; x++) {
      grid[bottomBorder][x].walls.bottom = true;
      if (bottomBorder + 1 < rows) grid[bottomBorder + 1][x].walls.top = true;
    }
    // Левая граница
    for (let y = topBorder; y <= bottomBorder; y++) {
      grid[y][leftBorder].walls.left = true;
      if (leftBorder > 0) grid[y][leftBorder - 1].walls.right = true;
    }
    // Правая граница
    for (let y = topBorder; y <= bottomBorder; y++) {
      grid[y][rightBorder].walls.right = true;
      if (rightBorder + 1 < cols) grid[y][rightBorder + 1].walls.left = true;
    }

    // Создаем единственный проход в лабиринт
    // Определяем возможные направления выхода
    const possibleExits: Array<{dir: string, canExit: boolean}> = [
      { dir: 'top', canExit: topBorder > 0 },
      { dir: 'bottom', canExit: bottomBorder + 1 < rows },
      { dir: 'left', canExit: leftBorder > 0 },
      { dir: 'right', canExit: rightBorder + 1 < cols }
    ];

    // Фильтруем только доступные направления
    const availableExits = possibleExits.filter(e => e.canExit);

    // Инициализируем дефолтным значением
    entranceCell = topLeft;

    if (availableExits.length > 0) {
      // Выбираем случайное направление
      const chosenExit = availableExits[Math.floor(Math.random() * availableExits.length)];

      switch (chosenExit.dir) {
        case 'top':
          const randomXTop = Math.floor(Math.random() * size) + leftBorder;
          entranceCell = { x: randomXTop, y: topBorder };
          grid[topBorder][randomXTop].walls.top = false;
          grid[topBorder - 1][randomXTop].walls.bottom = false;
          break;
        case 'bottom':
          const randomXBottom = Math.floor(Math.random() * size) + leftBorder;
          entranceCell = { x: randomXBottom, y: bottomBorder };
          grid[bottomBorder][randomXBottom].walls.bottom = false;
          grid[bottomBorder + 1][randomXBottom].walls.top = false;
          break;
        case 'left':
          const randomYLeft = Math.floor(Math.random() * size) + topBorder;
          entranceCell = { x: leftBorder, y: randomYLeft };
          grid[randomYLeft][leftBorder].walls.left = false;
          grid[randomYLeft][leftBorder - 1].walls.right = false;
          break;
        case 'right':
          const randomYRight = Math.floor(Math.random() * size) + topBorder;
          entranceCell = { x: rightBorder, y: randomYRight };
          grid[randomYRight][rightBorder].walls.right = false;
          grid[randomYRight][rightBorder + 1].walls.left = false;
          break;
      }
    }

    // Отмечаем ячейку выхода
    if (entranceCell.y < rows && entranceCell.x < cols) {
      grid[entranceCell.y][entranceCell.x].isExit = true;
    }
  }
  
  return {
    topLeft,
    size,
    entranceCell,
    type
  };
}

/**
 * Расчет манхэттенского расстояния между двумя центрами зон
 */
function calculateManhattanDistance(zone1: Zone, zone2: Zone): number {
  // Вычисляем центры зон
  const center1 = {
    x: zone1.topLeft.x + Math.floor(zone1.size / 2),
    y: zone1.topLeft.y + Math.floor(zone1.size / 2)
  };
  
  const center2 = {
    x: zone2.topLeft.x + Math.floor(zone2.size / 2),
    y: zone2.topLeft.y + Math.floor(zone2.size / 2)
  };
  
  // Возвращаем манхэттенское расстояние (по вертикали и горизонтали)
  return Math.abs(center1.x - center2.x) + Math.abs(center1.y - center2.y);
}

/**
 * Расчет евклидова (диагонального) расстояния между центрами зон
 */
function calculateEuclideanDistance(zone1: Zone, zone2: Zone): number {
  // Вычисляем центры зон
  const center1 = {
    x: zone1.topLeft.x + Math.floor(zone1.size / 2),
    y: zone1.topLeft.y + Math.floor(zone1.size / 2)
  };
  
  const center2 = {
    x: zone2.topLeft.x + Math.floor(zone2.size / 2),
    y: zone2.topLeft.y + Math.floor(zone2.size / 2)
  };
  
  // Вычисляем евклидово (диагональное) расстояние
  const dx = center1.x - center2.x;
  const dy = center1.y - center2.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Проверка, что зоны находятся на достаточном расстоянии друг от друга
 */
function areZonesFarEnough(zone1: Zone, zone2: Zone, minDistance: number = 5): boolean {
  // Проверяем и вертикальное и диагональное расстояние
  const manhattan = calculateManhattanDistance(zone1, zone2);
  const euclidean = calculateEuclideanDistance(zone1, zone2);

  // Минимальное расстояние должно быть больше чем минимально требуемое значение
  return manhattan >= minDistance && euclidean >= minDistance;
}

/**
 * Generate a circular/radial maze with concentric rings (optimized)
 */
function generateCircularMaze(
  canvasWidth: number,
  canvasHeight: number,
  complexity: number
): {
  wallSegments: WallSegment[],
  start: Position,
  end: Position,
  entranceZone: Zone,
  exitZone: Zone
} {
  const centerX = canvasWidth / 2;
  const centerY = canvasHeight / 2;
  const maxRadius = Math.min(centerX, centerY) * 0.9;

  // Optimized: Reduced number of rings and sectors for better performance
  const numRings = Math.max(4, Math.floor(4 + complexity * 6)); // 4-10 rings (was 5-15)
  const numSectors = Math.max(8, Math.floor(8 + complexity * 8)); // 8-16 sectors (was 8-24)

  const wallSegments: WallSegment[] = [];
  const ringSpacing = maxRadius / numRings;

  // Track which walls are removed (for maze paths)
  const radialWalls = new Set<string>(); // "ring,sector"
  const arcWalls = new Set<string>(); // "ring,sector"

  // Initialize all walls
  for (let ring = 0; ring < numRings; ring++) {
    for (let sector = 0; sector < numSectors; sector++) {
      radialWalls.add(`${ring},${sector}`);
      arcWalls.add(`${ring},${sector}`);
    }
  }

  // Generate maze using randomized DFS
  const visited = new Set<string>();
  const stack: {ring: number, sector: number}[] = [];

  // Start from center
  const startRing = 0;
  const startSector = 0;
  visited.add(`${startRing},${startSector}`);
  stack.push({ring: startRing, sector: startSector});

  // Optimized: Limit stack processing to prevent infinite loops
  let iterations = 0;
  const maxIterations = numRings * numSectors * 10;

  while (stack.length > 0 && iterations < maxIterations) {
    iterations++;
    const current = stack[stack.length - 1];
    const neighbors: {ring: number, sector: number, wall: string, isRadial: boolean}[] = [];

    // Check neighbors (inner ring, outer ring, left sector, right sector)
    if (current.ring > 0 && !visited.has(`${current.ring-3 - 1},${current.sector}`)) {
      neighbors.push({
        ring: current.ring - 1,
        sector: current.sector,
        wall: `${current.ring-3 - 1},${current.sector}`,
        isRadial: false
      });
    }
    if (current.ring < numRings - 1 && !visited.has(`${current.ring-3 + 1},${current.sector}`)) {
      neighbors.push({
        ring: current.ring + 1,
        sector: current.sector,
        wall: `${current.ring},${current.sector}`,
        isRadial: false
      });
    }
    const leftSector = (current.sector - 1 + numSectors) % numSectors;
    if (!visited.has(`${current.ring},${leftSector}`)) {
      neighbors.push({
        ring: current.ring,
        sector: leftSector,
        wall: `${current.ring},${leftSector}`,
        isRadial: true
      });
    }
    const rightSector = (current.sector + 1) % numSectors;
    if (!visited.has(`${current.ring},${rightSector}`)) {
      neighbors.push({
        ring: current.ring,
        sector: current.sector,
        wall: `${current.ring},${current.sector}`,
        isRadial: true
      });
    }

    if (neighbors.length > 0) {
      // Choose random neighbor
      const chosen = neighbors[Math.floor(Math.random() * neighbors.length)];

      // Remove wall
      if (chosen.isRadial) {
        radialWalls.delete(chosen.wall);
      } else {
        arcWalls.delete(chosen.wall);
      }

      // Visit neighbor
      visited.add(`${chosen.ring},${chosen.sector}`);
      stack.push({ring: chosen.ring, sector: chosen.sector});
    } else {
      stack.pop();
    }
  }

  // Create wall segments from remaining walls
  // Radial walls (spokes)
  radialWalls.forEach(wallKey => {
    const [ring, sector] = wallKey.split(',').map(Number);
    const angle = (sector / numSectors) * Math.PI * 2;
    const innerRadius = ring * ringSpacing;
    const outerRadius = (ring + 1) * ringSpacing;

    wallSegments.push({
      type: 'line',
      start: {
        x: centerX + Math.cos(angle) * innerRadius,
        y: centerY + Math.sin(angle) * innerRadius
      },
      end: {
        x: centerX + Math.cos(angle) * outerRadius,
        y: centerY + Math.sin(angle) * outerRadius
      }
    });
  });

  // Optimized: Arc walls with fewer segments (3 instead of 10)
  arcWalls.forEach(wallKey => {
    const [ring, sector] = wallKey.split(',').map(Number);
    const radius = (ring + 1) * ringSpacing;
    const startAngle = (sector / numSectors) * Math.PI * 2;
    const endAngle = ((sector + 1) / numSectors) * Math.PI * 2;

    // Draw arc as 3 line segments (optimized from 10)
    const segments = 3;
    for (let i = 0; i < segments; i++) {
      const a1 = startAngle + (endAngle - startAngle) * (i / segments);
      const a2 = startAngle + (endAngle - startAngle) * ((i + 1) / segments);

      wallSegments.push({
        type: 'line',
        start: {
          x: centerX + Math.cos(a1) * radius,
          y: centerY + Math.sin(a1) * radius
        },
        end: {
          x: centerX + Math.cos(a2) * radius,
          y: centerY + Math.sin(a2) * radius
        }
      });
    }
  });

  // Create entrance at center
  const entranceZone: Zone = {
    topLeft: { x: centerX - ringSpacing, y: centerY - ringSpacing },
    size: Math.floor(ringSpacing * 2),
    type: ZoneType.ENTRANCE,
    entranceCell: { x: centerX, y: centerY }
  };

  // Create exit at outer ring
  const exitAngle = Math.random() * Math.PI * 2;
  const exitZone: Zone = {
    topLeft: {
      x: centerX + Math.cos(exitAngle) * (maxRadius - ringSpacing * 1.5) - ringSpacing,
      y: centerY + Math.sin(exitAngle) * (maxRadius - ringSpacing * 1.5) - ringSpacing
    },
    size: Math.floor(ringSpacing * 2),
    type: ZoneType.EXIT,
    entranceCell: {
      x: centerX + Math.cos(exitAngle) * (maxRadius - ringSpacing),
      y: centerY + Math.sin(exitAngle) * (maxRadius - ringSpacing)
    }
  };

  return {
    wallSegments,
    start: entranceZone.entranceCell!,
    end: exitZone.entranceCell!,
    entranceZone,
    exitZone
  };
}

/**
 * Generate maze with random geometric shape
 */
export function generateMazeWithShape(
  canvasWidth: number,
  canvasHeight: number,
  cellSize: number,
  settings?: {
    pathComplexity?: number;
    deadEndDensity?: number;
    branchingFactor?: number;
    minPathLength?: number;
    wallRemovalChance?: number;
    shapeStyle?: 'orthogonal' | 'organic' | 'circular';
    curvature?: number;
    entranceZoneSize?: number;
    exitZoneSize?: number;
  }
): {
  maze: Cell[][];
  start: Position;
  end: Position;
  entranceZone: Zone;
  exitZone: Zone;
  wallSegments?: WallSegment[];
} {
  // Always use random shape
  const shape = getRandomShape();
  const complexity = settings?.pathComplexity || 0.5;

  // Generate maze with geometric shape
  const result = generateShapedMaze(canvasWidth, canvasHeight, cellSize, shape, complexity);

  // Create a dummy grid for compatibility
  const rows = Math.floor(canvasHeight / cellSize);
  const cols = Math.floor(canvasWidth / cellSize);
  const dummyGrid = createMazeGrid(rows, cols);

  return {
    maze: dummyGrid,
    start: result.start,
    end: result.end,
    entranceZone: result.entranceZone,
    exitZone: result.exitZone,
    wallSegments: result.walls
  };
}

function generateMaze(
  rows: number,
  cols: number,
  entranceZoneSize: number = 5,
  exitZoneSize: number = 5,
  settings?: {
    pathComplexity?: number;
    deadEndDensity?: number;
    branchingFactor?: number;
    minPathLength?: number;
    wallRemovalChance?: number;
    shapeStyle?: 'orthogonal' | 'organic' | 'circular';
    curvature?: number;
  }
): {
  maze: Cell[][],
  start: Position,
  end: Position,
  entranceZone: Zone,
  exitZone: Zone
} {
  // Validate grid dimensions
  if (rows <= 0 || cols <= 0) {
    throw new Error('Invalid grid dimensions');
  }

  // Add entropy to Math.random() by combining multiple sources of randomness
  // This ensures different mazes even with same parameters
  const entropy = Date.now() + performance.now() + Math.random() * 1000000;
  // Discard several random values to "warm up" the generator
  for (let i = 0; i < Math.floor((entropy % 20) + 10); i++) {
    Math.random();
  }

  const grid = createMazeGrid(rows, cols);
  
  // Применяем параметры сложности
  const complexity = settings?.pathComplexity || 0.5;
  const deadEndDensity = settings?.deadEndDensity || 0.5;
  const branchingFactor = settings?.branchingFactor || 3;
  const wallRemovalChance = settings?.wallRemovalChance || 0.05;

  // Создаем зоны входа и выхода (без проверки расстояния - полностью случайные позиции)
  const entranceZone = createZone(grid, ZoneType.ENTRANCE, entranceZoneSize, rows, cols);
  const exitZone = createZone(grid, ZoneType.EXIT, exitZoneSize, rows, cols);
  
  
  // Сбрасываем флаги visited перед генерацией лабиринта
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      grid[y][x].visited = false;
    }
  }
  
  // Генерируем лабиринт с учетом параметров сложности
  generateMazeWithComplexity(grid, rows, cols, complexity, deadEndDensity, branchingFactor, wallRemovalChance);
  
  // Reset visited flags (needed for path finding)
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      grid[y][x].visited = false;
    }
  }
  
  // Выбираем точки входа и выхода
  const start: Position = entranceZone.entranceCell || { x: 0, y: 0 };
  const end: Position = exitZone.entranceCell || { x: cols - 1, y: rows - 1 };

  return { maze: grid, start, end, entranceZone, exitZone };
}

function generateEntranceAndExit(
  maze: Cell[][],
  currentImagePair?: MazeImagePair
): { entrance: Zone; exit: Zone } {
  // Определяем размеры лабиринта
  const rows = maze.length;
  const cols = maze[0].length;
  
  // Вычисляем оптимальный размер зоны
  const zoneSize = Math.min(Math.floor(rows / 4), Math.floor(cols / 4));
  
  // Инициализируем зоны
  const entrance: Zone = {
    topLeft: { x: 0, y: 0 },
    size: zoneSize,
    type: ZoneType.ENTRANCE,
    image: currentImagePair?.start
  };
  const exit: Zone = {
    topLeft: { x: 0, y: 0 },
    size: zoneSize,
    type: ZoneType.EXIT,
    image: currentImagePair?.finish
  };
  
  // Попытка найти подходящие зоны
  const maxAttempts = 10;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    // Генерируем случайные координаты для входа и выхода
    const entranceX = Math.floor(Math.random() * (cols - zoneSize));
    const entranceY = Math.floor(Math.random() * (rows - zoneSize));
    
    const exitX = Math.floor(Math.random() * (cols - zoneSize));
    const exitY = Math.floor(Math.random() * (rows - zoneSize));
    
    // Проверяем, что зоны не пересекаются
    if (Math.abs(entranceX - exitX) < zoneSize || 
        Math.abs(entranceY - exitY) < zoneSize) {
      continue;
    }
    
    // Проверяем, что зоны находятся в пределах лабиринта
    if (entranceX + zoneSize >= cols || 
        entranceY + zoneSize >= rows ||
        exitX + zoneSize >= cols ||
        exitY + zoneSize >= rows) {
      continue;
    }
    
    // Если все проверки пройдены, устанавливаем координаты зон
    entrance.topLeft = { x: entranceX, y: entranceY };
    exit.topLeft = { x: exitX, y: exitY };
    
    // Проверяем, что зоны не перекрывают стены
    let isValid = true;
    
    // Проверяем входную зону
    for (let y = entranceY; y < entranceY + zoneSize; y++) {
      for (let x = entranceX; x < entranceX + zoneSize; x++) {
        if (maze[y][x].walls.top || maze[y][x].walls.right || 
            maze[y][x].walls.bottom || maze[y][x].walls.left) {
          isValid = false;
          break;
        }
      }
      if (!isValid) break;
    }
    
    // Проверяем выходную зону
    for (let y = exitY; y < exitY + zoneSize; y++) {
      for (let x = exitX; x < exitX + zoneSize; x++) {
        if (maze[y][x].walls.top || maze[y][x].walls.right || 
            maze[y][x].walls.bottom || maze[y][x].walls.left) {
          isValid = false;
          break;
        }
      }
      if (!isValid) break;
    }
    
    if (isValid) {
      break;
    }
  }
  
  return { entrance, exit };
}

/**
 * Convert straight walls to curved walls with Bezier curves
 */
export function addCurvatureToWalls(
  maze: Cell[][],
  cellSize: number,
  curvature: number
): WallSegment[] {
  if (curvature === 0) {
    // Return straight line segments for orthogonal mazes
    return convertCellsToLineSegments(maze, cellSize);
  }

  const wallSegments: WallSegment[] = [];
  const rows = maze.length;
  const cols = maze[0].length;

  let curvedCount = 0;
  let straightCount = 0;

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const cell = maze[y][x];

      // Top wall
      if (cell.walls.top) {
        const start: Position = { x: x * cellSize, y: y * cellSize };
        const end: Position = { x: (x + 1) * cellSize, y: y * cellSize };

        if (Math.random() < curvature) {
          // Add bezier curve with random control point
          // Increased offset multiplier for more visible curves
          const controlOffset = (Math.random() - 0.5) * cellSize * curvature * 0.5;
          const control1: Position = {
            x: (start.x + end.x) / 2,
            y: start.y + controlOffset
          };
          wallSegments.push({ type: 'bezier', start, end, control1 });
          curvedCount++;
        } else {
          wallSegments.push({ type: 'line', start, end });
          straightCount++;
        }
      }

      // Right wall
      if (cell.walls.right) {
        const start: Position = { x: (x + 1) * cellSize, y: y * cellSize };
        const end: Position = { x: (x + 1) * cellSize, y: (y + 1) * cellSize };

        if (Math.random() < curvature) {
          const controlOffset = (Math.random() - 0.5) * cellSize * curvature * 0.5;
          const control1: Position = {
            x: start.x + controlOffset,
            y: (start.y + end.y) / 2
          };
          wallSegments.push({ type: 'bezier', start, end, control1 });
          curvedCount++;
        } else {
          wallSegments.push({ type: 'line', start, end });
          straightCount++;
        }
      }

      // Bottom wall
      if (cell.walls.bottom) {
        const start: Position = { x: x * cellSize, y: (y + 1) * cellSize };
        const end: Position = { x: (x + 1) * cellSize, y: (y + 1) * cellSize };

        if (Math.random() < curvature) {
          const controlOffset = (Math.random() - 0.5) * cellSize * curvature * 0.5;
          const control1: Position = {
            x: (start.x + end.x) / 2,
            y: start.y + controlOffset
          };
          wallSegments.push({ type: 'bezier', start, end, control1 });
          curvedCount++;
        } else {
          wallSegments.push({ type: 'line', start, end });
          straightCount++;
        }
      }

      // Left wall
      if (cell.walls.left) {
        const start: Position = { x: x * cellSize, y: y * cellSize };
        const end: Position = { x: x * cellSize, y: (y + 1) * cellSize };

        if (Math.random() < curvature) {
          const controlOffset = (Math.random() - 0.5) * cellSize * curvature * 0.5;
          const control1: Position = {
            x: start.x + controlOffset,
            y: (start.y + end.y) / 2
          };
          wallSegments.push({ type: 'bezier', start, end, control1 });
          curvedCount++;
        } else {
          wallSegments.push({ type: 'line', start, end });
          straightCount++;
        }
      }
    }
  }

  return wallSegments;
}

/**
 * Convert cell-based walls to line segments (for orthogonal mazes)
 */
function convertCellsToLineSegments(maze: Cell[][], cellSize: number): WallSegment[] {
  const wallSegments: WallSegment[] = [];
  const rows = maze.length;
  const cols = maze[0].length;

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const cell = maze[y][x];

      if (cell.walls.top) {
        wallSegments.push({
          type: 'line',
          start: { x: x * cellSize, y: y * cellSize },
          end: { x: (x + 1) * cellSize, y: y * cellSize }
        });
      }
      if (cell.walls.right) {
        wallSegments.push({
          type: 'line',
          start: { x: (x + 1) * cellSize, y: y * cellSize },
          end: { x: (x + 1) * cellSize, y: (y + 1) * cellSize }
        });
      }
      if (cell.walls.bottom) {
        wallSegments.push({
          type: 'line',
          start: { x: x * cellSize, y: (y + 1) * cellSize },
          end: { x: (x + 1) * cellSize, y: (y + 1) * cellSize }
        });
      }
      if (cell.walls.left) {
        wallSegments.push({
          type: 'line',
          start: { x: x * cellSize, y: y * cellSize },
          end: { x: x * cellSize, y: (y + 1) * cellSize }
        });
      }
    }
  }

  return wallSegments;
}
